import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { getJson, mutateJson } from '../api/client';
import { Field, Input, Select } from '../components/form';
import { Alert, Button, Card, EmptyState, Spinner } from '../components/ui';
import { dataBr } from '../lib/format';
import styles from './screens.module.css';

interface Unidade {
  id: number;
  numero: string;
  ativo: boolean;
}
interface Pessoa {
  id: number;
  nome: string;
}
interface Vinculo {
  id: number;
  unidadeId: number;
  pessoaId: number;
  papel: string;
  inicio: string | null;
}
interface RespostaVinculo {
  vinculo?: Vinculo;
  error?: string;
}

type Carga =
  | { estado: 'carregando' }
  | { estado: 'erro' }
  | { estado: 'ok'; unidades: Unidade[]; pessoas: Pessoa[] };

type Campo = 'pessoa' | 'papel';

/** Papéis aceitos pela API (`VINCULO_PAPEIS`) e seus rótulos. */
const PAPEL_LABEL: Record<string, string> = {
  proprietario: 'Proprietário',
  inquilino: 'Inquilino',
  pagante: 'Pagante',
};
const PAPEIS = Object.keys(PAPEL_LABEL);

/** Erros de validação da API que pertencem a um campo do formulário. */
const ERRO_DE_CAMPO: Record<string, { campo: Campo; mensagem: string }> = {
  vinculo_duplicado: { campo: 'pessoa', mensagem: 'Essa pessoa já tem esse papel nesta unidade.' },
  pessoa_not_found: { campo: 'pessoa', mensagem: 'Pessoa não encontrada. Recarregue a página.' },
  invalid_papel: { campo: 'papel', mensagem: 'Escolha o papel.' },
};

/** Demais erros da API (código → mensagem), mostrados acima da lista. */
const ERRO_GERAL: Record<string, string> = {
  tenant_suspended: 'Condomínio suspenso — somente leitura.',
  unidade_not_found: 'Unidade não encontrada. Recarregue a página.',
  not_found: 'Vínculo já encerrado. A lista foi atualizada.',
};
const ERRO_GERAL_PADRAO = 'Não foi possível salvar. Tente novamente.';

/** Unidade pedida na URL (`/vinculos?unidade=ID`, vinda da linha de Unidades). */
function unidadeDaUrl(): number | null {
  const id = Number(new URLSearchParams(window.location.search).get('unidade'));
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * Vínculos pessoa↔unidade (RF-A03): proprietário, inquilino e pagante por unidade.
 * `editavel` só para o síndico de condomínio ativo: POST/DELETE são
 * `requireRole('sindico')`, o contábil é somente leitura e o tenant suspenso também.
 * A API lista só os vigentes de uma unidade e não traz nomes; a tela cruza com
 * /api/pessoas.
 */
export function Vinculos({ editavel }: { editavel: boolean }) {
  const [carga, setCarga] = useState<Carga>({ estado: 'carregando' });
  const [unidadeId, setUnidadeId] = useState<number | null>(unidadeDaUrl);
  const [vinculos, setVinculos] = useState<Vinculo[] | 'carregando' | 'erro'>('carregando');
  // Unidade selecionada no momento: respostas de outra unidade (troca rápida, encerrar
  // ou vincular terminando depois da troca) são descartadas.
  const unidadeAtual = useRef(unidadeId);
  unidadeAtual.current = unidadeId;
  const [aviso, setAviso] = useState<{ kind: 'success' | 'danger'; texto: string } | null>(null);

  useEffect(() => {
    void Promise.all([
      getJson<{ unidades: Unidade[] }>('/api/unidades'),
      getJson<{ pessoas: Pessoa[] }>('/api/pessoas'),
    ]).then(([u, p]) => {
      if (!u || !p) {
        setCarga({ estado: 'erro' });
        return;
      }
      setCarga({ estado: 'ok', unidades: u.unidades, pessoas: p.pessoas });
      // Sem unidade válida na URL, abre na primeira da lista.
      setUnidadeId((atual) =>
        u.unidades.some((x) => x.id === atual) ? atual : (u.unidades[0]?.id ?? null),
      );
    });
  }, []);

  const carregarVinculos = useCallback(async () => {
    if (unidadeId === null) return;
    const dados = await getJson<{ vinculos: Vinculo[] }>(
      `/api/vinculos?unidadeId=${encodeURIComponent(unidadeId)}`,
    );
    if (unidadeAtual.current !== unidadeId) return;
    setVinculos(dados ? dados.vinculos : 'erro');
  }, [unidadeId]);

  useEffect(() => {
    setVinculos('carregando');
    void carregarVinculos();
  }, [carregarVinculos]);

  /** Erro da API fora dos campos; vínculo já encerrado recarrega a lista. */
  async function tratarFalha(codigo?: string) {
    setAviso({ kind: 'danger', texto: (codigo && ERRO_GERAL[codigo]) || ERRO_GERAL_PADRAO });
    if (codigo === 'not_found') await carregarVinculos();
  }

  async function encerrar(v: Vinculo, nome: string) {
    setAviso(null);
    const res = await mutateJson<{ error?: string }>(
      `/api/vinculos/${encodeURIComponent(v.id)}`,
      {},
      'DELETE',
    );
    if (!res.ok) {
      await tratarFalha(res.data?.error);
      return;
    }
    setAviso({ kind: 'success', texto: `Vínculo de ${nome} encerrado.` });
    await carregarVinculos();
  }

  if (carga.estado === 'carregando') return <Spinner />;
  if (carga.estado === 'erro') {
    return <Alert kind="danger">Não foi possível carregar unidades e pessoas.</Alert>;
  }
  const { unidades, pessoas } = carga;
  if (unidades.length === 0) {
    return <EmptyState>Cadastre uma unidade antes de criar vínculos.</EmptyState>;
  }
  const nomePessoa = (id: number) => pessoas.find((p) => p.id === id)?.nome ?? `Pessoa #${id}`;

  return (
    <div className={styles.stack}>
      <Field label="Unidade">
        {(id) => (
          <Select
            id={id}
            value={unidadeId ?? ''}
            onChange={(e) => {
              setAviso(null);
              setUnidadeId(Number(e.target.value));
            }}
            style={{ maxWidth: '14rem' }}
          >
            {unidades.map((u) => (
              <option key={u.id} value={u.id}>
                {u.numero}
                {u.ativo ? '' : ' (inativa)'}
              </option>
            ))}
          </Select>
        )}
      </Field>

      {aviso ? <Alert kind={aviso.kind}>{aviso.texto}</Alert> : null}

      {editavel && unidadeId !== null ? (
        <VinculoForm
          key={unidadeId}
          unidadeId={unidadeId}
          pessoas={pessoas}
          onSalvo={async (nome) => {
            setAviso({ kind: 'success', texto: `${nome} vinculado à unidade.` });
            await carregarVinculos();
          }}
          onErroGeral={tratarFalha}
        />
      ) : null}

      {vinculos === 'carregando' ? (
        <Spinner />
      ) : vinculos === 'erro' ? (
        <Alert kind="danger">Não foi possível carregar os vínculos.</Alert>
      ) : vinculos.length === 0 ? (
        <EmptyState>Nenhum vínculo vigente nesta unidade.</EmptyState>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Pessoa</th>
                <th>Papel</th>
                <th>Início</th>
                {editavel ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {vinculos.map((v) => (
                <tr key={v.id}>
                  <td>{nomePessoa(v.pessoaId)}</td>
                  <td>{PAPEL_LABEL[v.papel] ?? v.papel}</td>
                  <td>{v.inicio ? dataBr(v.inicio) : '—'}</td>
                  {editavel ? (
                    <td>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Encerrar vínculo de ${nomePessoa(v.pessoaId)}`}
                        onClick={() => void encerrar(v, nomePessoa(v.pessoaId))}
                      >
                        Encerrar
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Formulário de novo vínculo na unidade; erros de validação vão para o campo. */
function VinculoForm({
  unidadeId,
  pessoas,
  onSalvo,
  onErroGeral,
}: {
  unidadeId: number;
  pessoas: Pessoa[];
  onSalvo: (nomePessoa: string) => Promise<void>;
  /** Erro da API fora dos campos (código; ausente = falha de rede). */
  onErroGeral: (codigo?: string) => Promise<void>;
}) {
  const [pessoaId, setPessoaId] = useState('');
  const [papel, setPapel] = useState(PAPEIS[0] ?? '');
  const [inicio, setInicio] = useState('');
  const [erros, setErros] = useState<Partial<Record<Campo, string>>>({});
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (!pessoaId) {
      setErros({ pessoa: 'Escolha a pessoa.' });
      return;
    }
    setErros({});
    setEnviando(true);
    const res = await mutateJson<RespostaVinculo>('/api/vinculos', {
      unidadeId,
      pessoaId: Number(pessoaId),
      papel,
      ...(inicio ? { inicio } : {}),
    });
    setEnviando(false);

    if (res.ok) {
      const nome = pessoas.find((p) => p.id === Number(pessoaId))?.nome ?? 'Pessoa';
      setPessoaId('');
      setInicio('');
      await onSalvo(nome);
      return;
    }
    const codigo = res.data?.error;
    const deCampo = codigo ? ERRO_DE_CAMPO[codigo] : undefined;
    if (deCampo) setErros({ [deCampo.campo]: deCampo.mensagem });
    else await onErroGeral(codigo);
  }

  return (
    <Card title="Novo vínculo">
      <form onSubmit={enviar} noValidate>
        <div className={styles.row}>
          <Field label="Pessoa" error={erros.pessoa}>
            {(id) => (
              <Select id={id} value={pessoaId} onChange={(e) => setPessoaId(e.target.value)}>
                <option value="">Escolha…</option>
                {pessoas.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Papel" error={erros.papel}>
            {(id) => (
              <Select id={id} value={papel} onChange={(e) => setPapel(e.target.value)}>
                {PAPEIS.map((p) => (
                  <option key={p} value={p}>
                    {PAPEL_LABEL[p]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Início">
            {(id) => (
              <Input
                id={id}
                type="date"
                value={inicio}
                onChange={(e) => setInicio(e.target.value)}
              />
            )}
          </Field>
        </div>
        <Button type="submit" disabled={enviando}>
          Vincular
        </Button>
      </form>
    </Card>
  );
}
